import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';

// ── Enums ──────────────────────────────────────────────────────────────────

export type VotingMode = 'TRACKED' | 'OPEN' | 'COOKIE';
export type FeatureRequestStatus =
	| 'OPEN'
	| 'PLANNED'
	| 'IN_PROGRESS'
	| 'COMPLETED'
	| 'REJECTED';

export const VOTING_MODES: VotingMode[] = ['TRACKED', 'OPEN', 'COOKIE'];
export const FR_STATUSES: FeatureRequestStatus[] = [
	'OPEN',
	'PLANNED',
	'IN_PROGRESS',
	'COMPLETED',
	'REJECTED',
];

// ── Types ──────────────────────────────────────────────────────────────────

export interface TenantObject {
	id: string;
	name: string;
	createdAt: string;
	updatedAt: string;
}

export interface ProjectObject {
	id: string;
	tenantId: string;
	tenantName?: string | null;
	name: string;
	description?: string | null;
	votingMode: string;
	status: string;
	allowedOrigins: string[];
	hmacSecret?: string | null;
	canEdit: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface AdminFeatureRequest {
	id: string;
	projectId: string;
	title: string;
	description?: string | null;
	status: string;
	createdByExtRefId?: string | null;
	createdByName?: string | null;
	upvoteCount: number;
	hide: boolean;
	isFlaggedByModeration: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface AccountObject {
	id: string;
	email: string;
	name?: string | null;
	role: string;
	createdAt: string;
}

export interface ProjectMember {
	id: string;
	projectId: string;
	accountId: string;
	email: string;
	name?: string | null;
	role: string;
	createdAt: string;
}

export interface NoteObject {
	id: string;
	featureRequestId: string;
	accountId?: string | null;
	body: string;
	createdAt: string;
	updatedAt: string;
}

// ── Fragments ─────────────────────────────────────────────────────────────

const PROJECT_FIELDS = gql`
	fragment ProjectFields on ProjectObject {
		id
		tenantId
		tenantName
		name
		description
		votingMode
		status
		allowedOrigins
		hmacSecret
		canEdit
		createdAt
		updatedAt
	}
`;

const ADMIN_FR_FIELDS = gql`
	fragment AdminFRFields on AdminFeatureRequestObject {
		id
		projectId
		title
		description
		status
		createdByExtRefId
		createdByName
		upvoteCount
		hide
		isFlaggedByModeration
		createdAt
		updatedAt
	}
`;

const NOTE_FIELDS = gql`
	fragment NoteFields on NoteObject {
		id
		featureRequestId
		accountId
		body
		createdAt
		updatedAt
	}
`;

// ── Documents ─────────────────────────────────────────────────────────────

const ADMIN_TENANTS_QUERY = gql`
	query AdminTenants {
		adminTenants {
			id
			name
			createdAt
			updatedAt
		}
	}
`;

const ADMIN_PROJECTS_QUERY = gql`
	${PROJECT_FIELDS}
	query AdminProjects {
		adminProjects {
			...ProjectFields
		}
	}
`;

const ADMIN_PROJECT_QUERY = gql`
	${PROJECT_FIELDS}
	query AdminProject($id: ID!) {
		adminProject(id: $id) {
			...ProjectFields
		}
	}
`;

const ADMIN_CREATE_PROJECT_MUTATION = gql`
	${PROJECT_FIELDS}
	mutation AdminCreateProject($input: CreateProjectInput!) {
		adminCreateProject(input: $input) {
			...ProjectFields
		}
	}
`;

const ADMIN_UPDATE_PROJECT_MUTATION = gql`
	${PROJECT_FIELDS}
	mutation AdminUpdateProject($id: ID!, $input: UpdateProjectInput!) {
		adminUpdateProject(id: $id, input: $input) {
			...ProjectFields
		}
	}
`;

const ADMIN_FEATURE_REQUESTS_QUERY = gql`
	${ADMIN_FR_FIELDS}
	query AdminFeatureRequests($projectId: ID!) {
		adminFeatureRequests(projectId: $projectId) {
			...AdminFRFields
		}
	}
`;

const ADMIN_UPDATE_FR_MUTATION = gql`
	${ADMIN_FR_FIELDS}
	mutation AdminUpdateFeatureRequest($input: UpdateFeatureRequestInput!) {
		adminUpdateFeatureRequest(input: $input) {
			...AdminFRFields
		}
	}
`;

const ADMIN_UPDATE_FR_STATUS_MUTATION = gql`
	${ADMIN_FR_FIELDS}
	mutation AdminUpdateFeatureRequestStatus($input: UpdateFeatureRequestStatusInput!) {
		adminUpdateFeatureRequestStatus(input: $input) {
			...AdminFRFields
		}
	}
`;

const ADMIN_TOGGLE_HIDE_FR_MUTATION = gql`
	${ADMIN_FR_FIELDS}
	mutation AdminToggleHideFeatureRequest($input: ToggleHideFeatureRequestInput!) {
		adminToggleHideFeatureRequest(input: $input) {
			...AdminFRFields
		}
	}
`;

const ADMIN_TOGGLE_MODERATION_FLAG_MUTATION = gql`
	${ADMIN_FR_FIELDS}
	mutation AdminToggleModerationFlag($input: ToggleModerationFlagInput!) {
		adminToggleModerationFlag(input: $input) {
			...AdminFRFields
		}
	}
`;

const ADMIN_NOTES_QUERY = gql`
	${NOTE_FIELDS}
	query AdminNotes($featureRequestId: ID!) {
		adminNotes(featureRequestId: $featureRequestId) {
			...NoteFields
		}
	}
`;

const ADMIN_CREATE_NOTE_MUTATION = gql`
	${NOTE_FIELDS}
	mutation AdminCreateNote($input: CreateNoteInput!) {
		adminCreateNote(input: $input) {
			...NoteFields
		}
	}
`;

const ADMIN_UPDATE_NOTE_MUTATION = gql`
	${NOTE_FIELDS}
	mutation AdminUpdateNote($id: ID!, $body: String!) {
		adminUpdateNote(id: $id, body: $body) {
			...NoteFields
		}
	}
`;

const ADMIN_DELETE_NOTE_MUTATION = gql`
	mutation AdminDeleteNote($id: ID!) {
		adminDeleteNote(id: $id)
	}
`;

const ADMIN_ACCOUNTS_QUERY = gql`
	query AdminAccounts {
		adminAccounts {
			id
			email
			name
			role
			createdAt
		}
	}
`;

const ADMIN_UPDATE_ACCOUNT_ROLE_MUTATION = gql`
	mutation AdminUpdateAccountRole($input: UpdateAccountRoleInput!) {
		adminUpdateAccountRole(input: $input) {
			id
			email
			name
			role
			createdAt
		}
	}
`;

const MEMBER_FIELDS = gql`
	fragment MemberFields on ProjectMemberObject {
		id
		projectId
		accountId
		email
		name
		role
		createdAt
	}
`;

const ADMIN_PROJECT_MEMBERS_QUERY = gql`
	${MEMBER_FIELDS}
	query AdminProjectMembers($projectId: ID!) {
		adminProjectMembers(projectId: $projectId) {
			...MemberFields
		}
	}
`;

const ADMIN_ADD_PROJECT_MEMBER_MUTATION = gql`
	${MEMBER_FIELDS}
	mutation AdminAddProjectMember($input: AddProjectMemberInput!) {
		adminAddProjectMember(input: $input) {
			...MemberFields
		}
	}
`;

const ADMIN_REMOVE_PROJECT_MEMBER_MUTATION = gql`
	${MEMBER_FIELDS}
	mutation AdminRemoveProjectMember($input: RemoveProjectMemberInput!) {
		adminRemoveProjectMember(input: $input) {
			...MemberFields
		}
	}
`;

const ME_QUERY = gql`
	query AdminMe {
		me {
			id
			email
			role
		}
	}
`;

const LOGIN_MUTATION = gql`
	mutation AdminLogin($input: LoginDto!) {
		login(input: $input) {
			success
			sessionId
		}
	}
`;

const LOGOUT_MUTATION = gql`
	mutation AdminLogout {
		logout
	}
`;

const ADMIN_REGENERATE_HMAC_MUTATION = gql`
	${PROJECT_FIELDS}
	mutation AdminRegenerateProjectHmacSecret($id: ID!) {
		adminRegenerateProjectHmacSecret(id: $id) {
			...ProjectFields
		}
	}
`;

// ── Hooks ─────────────────────────────────────────────────────────────────

export function useAdminTenants() {
	return useQuery<{ adminTenants: TenantObject[] }>(ADMIN_TENANTS_QUERY);
}

export function useAdminMe() {
	return useQuery<{
		me: {
			id: string;
			email: string;
			role: string;
			name?: string | null;
		} | null;
	}>(ME_QUERY, {
		fetchPolicy: 'network-only',
		errorPolicy: 'ignore',
	});
}

export function useAdminLogin() {
	return useMutation<
		{ login: { success: boolean } },
		{ input: { email: string; password: string } }
	>(LOGIN_MUTATION);
}

export function useAdminRegister() {
	return useMutation<
		{ register: { success: boolean } },
		{ input: { email: string; password: string; name?: string } }
	>(
		gql`
			mutation AdminRegister($input: RegisterDto!) {
				register(input: $input) {
					success
				}
			}
		`,
	);
}

export function useAdminLogout() {
	return useMutation<{ logout: boolean }>(LOGOUT_MUTATION);
}

export function useAdminProjects() {
	return useQuery<{ adminProjects: ProjectObject[] }>(ADMIN_PROJECTS_QUERY);
}

export function useAdminProject(id: string) {
	return useQuery<{ adminProject: ProjectObject | null }>(
		ADMIN_PROJECT_QUERY,
		{
			variables: { id },
			skip: !id,
		},
	);
}

export function useAdminCreateProject() {
	return useMutation<
		{ adminCreateProject: ProjectObject },
		{
			input: Partial<ProjectObject> & {
				tenantId: string;
				name: string;
			};
		}
	>(ADMIN_CREATE_PROJECT_MUTATION, {
		update(cache, { data }) {
			if (!data?.adminCreateProject) return;
			cache.updateQuery<{ adminProjects: ProjectObject[] }>(
				{ query: ADMIN_PROJECTS_QUERY },
				(existing) => {
					if (!existing) return existing;
					return {
						adminProjects: [
							...existing.adminProjects,
							data.adminCreateProject,
						],
					};
				},
			);
		},
	});
}

export function useAdminUpdateProject() {
	return useMutation<
		{ adminUpdateProject: ProjectObject | null },
		{ id: string; input: Partial<ProjectObject> }
	>(ADMIN_UPDATE_PROJECT_MUTATION, {
		update(cache, { data }, { variables }) {
			if (!data?.adminUpdateProject || !variables?.id) return;
			cache.updateQuery<{ adminProjects: ProjectObject[] }>(
				{ query: ADMIN_PROJECTS_QUERY },
				(existing) => {
					if (!existing) return existing;
					return {
						adminProjects: existing.adminProjects.map((p) =>
							p.id === variables.id
								? { ...p, ...data.adminUpdateProject }
								: p,
						),
					};
				},
			);
		},
	});
}

export function useAdminFeatureRequests(projectId: string) {
	return useQuery<{ adminFeatureRequests: AdminFeatureRequest[] }>(
		ADMIN_FEATURE_REQUESTS_QUERY,
		{
			variables: { projectId },
			skip: !projectId,
		},
	);
}

export function useAdminRegenerateHmacSecret() {
	return useMutation<
		{ adminRegenerateProjectHmacSecret: ProjectObject | null },
		{ id: string }
	>(ADMIN_REGENERATE_HMAC_MUTATION);
}

export function useAdminUpdateFR() {
	return useMutation<
		{ adminUpdateFeatureRequest: AdminFeatureRequest | null },
		{
			input: {
				featureRequestId: string;
				title?: string;
				description?: string;
			};
		}
	>(ADMIN_UPDATE_FR_MUTATION, {
		update(cache, { data }, { variables }) {
			if (
				!data?.adminUpdateFeatureRequest ||
				!variables?.input.featureRequestId
			)
				return;
			const updated = data.adminUpdateFeatureRequest;
			cache.updateQuery<{ adminFeatureRequests: AdminFeatureRequest[] }>(
				{
					query: ADMIN_FEATURE_REQUESTS_QUERY,
					variables: { projectId: updated.projectId },
				},
				(existing) => {
					if (!existing) return existing;
					return {
						adminFeatureRequests: existing.adminFeatureRequests.map(
							(fr) =>
								fr.id === updated.id
									? { ...fr, ...updated }
									: fr,
						),
					};
				},
			);
		},
	});
}

export function useAdminUpdateFRStatus() {
	return useMutation<
		{ adminUpdateFeatureRequestStatus: AdminFeatureRequest | null },
		{ input: { featureRequestId: string; status: string } }
	>(ADMIN_UPDATE_FR_STATUS_MUTATION, {
		update(cache, { data }, { variables }) {
			if (
				!data?.adminUpdateFeatureRequestStatus ||
				!variables?.input.featureRequestId
			)
				return;
			const updated = data.adminUpdateFeatureRequestStatus;
			cache.updateQuery<{ adminFeatureRequests: AdminFeatureRequest[] }>(
				{
					query: ADMIN_FEATURE_REQUESTS_QUERY,
					variables: { projectId: updated.projectId },
				},
				(existing) => {
					if (!existing) return existing;
					return {
						adminFeatureRequests: existing.adminFeatureRequests.map(
							(fr) =>
								fr.id === updated.id
									? { ...fr, ...updated }
									: fr,
						),
					};
				},
			);
		},
	});
}

export function useAdminToggleHideFR() {
	return useMutation<
		{ adminToggleHideFeatureRequest: AdminFeatureRequest | null },
		{ input: { featureRequestId: string; hide: boolean } }
	>(ADMIN_TOGGLE_HIDE_FR_MUTATION, {
		update(cache, { data }, { variables }) {
			if (
				!data?.adminToggleHideFeatureRequest ||
				!variables?.input.featureRequestId
			)
				return;
			const updated = data.adminToggleHideFeatureRequest;
			cache.updateQuery<{ adminFeatureRequests: AdminFeatureRequest[] }>(
				{
					query: ADMIN_FEATURE_REQUESTS_QUERY,
					variables: { projectId: updated.projectId },
				},
				(existing) => {
					if (!existing) return existing;
					return {
						adminFeatureRequests: existing.adminFeatureRequests.map(
							(fr) =>
								fr.id === updated.id
									? { ...fr, ...updated }
									: fr,
						),
					};
				},
			);
		},
	});
}

export function useAdminToggleModerationFlagFR() {
	return useMutation<
		{ adminToggleModerationFlag: AdminFeatureRequest | null },
		{ input: { featureRequestId: string; isFlaggedByModeration: boolean } }
	>(ADMIN_TOGGLE_MODERATION_FLAG_MUTATION, {
		update(cache, { data }, { variables }) {
			if (
				!data?.adminToggleModerationFlag ||
				!variables?.input.featureRequestId
			)
				return;
			const updated = data.adminToggleModerationFlag;
			cache.updateQuery<{ adminFeatureRequests: AdminFeatureRequest[] }>(
				{
					query: ADMIN_FEATURE_REQUESTS_QUERY,
					variables: { projectId: updated.projectId },
				},
				(existing) => {
					if (!existing) return existing;
					return {
						adminFeatureRequests: existing.adminFeatureRequests.map(
							(fr) =>
								fr.id === updated.id
									? { ...fr, ...updated }
									: fr,
						),
					};
				},
			);
		},
	});
}

export function useAdminNotes(featureRequestId: string) {
	return useQuery<{ adminNotes: NoteObject[] }>(ADMIN_NOTES_QUERY, {
		variables: { featureRequestId },
		skip: !featureRequestId,
	});
}

export function useAdminCreateNote() {
	return useMutation<
		{ adminCreateNote: NoteObject },
		{ input: { featureRequestId: string; body: string } }
	>(ADMIN_CREATE_NOTE_MUTATION, {
		update(cache, { data }, { variables }) {
			if (!data?.adminCreateNote || !variables?.input.featureRequestId)
				return;
			cache.updateQuery<{ adminNotes: NoteObject[] }>(
				{
					query: ADMIN_NOTES_QUERY,
					variables: {
						featureRequestId: variables.input.featureRequestId,
					},
				},
				(existing) => {
					if (!existing) return existing;
					return {
						adminNotes: [
							...existing.adminNotes,
							data.adminCreateNote,
						],
					};
				},
			);
		},
	});
}

export function useAdminUpdateNote() {
	return useMutation<
		{ adminUpdateNote: NoteObject | null },
		{ id: string; body: string }
	>(ADMIN_UPDATE_NOTE_MUTATION);
}

export function useAdminDeleteNote() {
	return useMutation<{ adminDeleteNote: boolean }, { id: string }>(
		ADMIN_DELETE_NOTE_MUTATION,
	);
}

export function useAdminAccounts() {
	return useQuery<{ adminAccounts: AccountObject[] }>(ADMIN_ACCOUNTS_QUERY);
}

export function useAdminProjectMembers(projectId: string) {
	return useQuery<{ adminProjectMembers: ProjectMember[] }>(
		ADMIN_PROJECT_MEMBERS_QUERY,
		{ variables: { projectId }, skip: !projectId },
	);
}

export function useAdminAddProjectMember() {
	return useMutation<
		{ adminAddProjectMember: ProjectMember[] },
		{ input: { projectId: string; email: string; role: string } }
	>(ADMIN_ADD_PROJECT_MEMBER_MUTATION, {
		update(cache, { data }, { variables }) {
			if (!data?.adminAddProjectMember || !variables?.input.projectId)
				return;
			cache.updateQuery<{ adminProjectMembers: ProjectMember[] }>(
				{
					query: ADMIN_PROJECT_MEMBERS_QUERY,
					variables: { projectId: variables.input.projectId },
				},
				() => ({ adminProjectMembers: data.adminAddProjectMember }),
			);
		},
	});
}

export function useAdminRemoveProjectMember() {
	return useMutation<
		{ adminRemoveProjectMember: ProjectMember[] },
		{ input: { projectId: string; accountId: string } }
	>(ADMIN_REMOVE_PROJECT_MEMBER_MUTATION, {
		update(cache, { data }, { variables }) {
			if (!data?.adminRemoveProjectMember || !variables?.input.projectId)
				return;
			cache.updateQuery<{ adminProjectMembers: ProjectMember[] }>(
				{
					query: ADMIN_PROJECT_MEMBERS_QUERY,
					variables: { projectId: variables.input.projectId },
				},
				() => ({ adminProjectMembers: data.adminRemoveProjectMember }),
			);
		},
	});
}

export function useAdminUpdateAccountRole() {
	return useMutation<
		{ adminUpdateAccountRole: AccountObject },
		{ input: { accountId: string; role: string } }
	>(ADMIN_UPDATE_ACCOUNT_ROLE_MUTATION, {
		update(cache, { data }) {
			if (!data?.adminUpdateAccountRole) return;
			const updated = data.adminUpdateAccountRole;
			cache.updateQuery<{ adminAccounts: AccountObject[] }>(
				{ query: ADMIN_ACCOUNTS_QUERY },
				(existing) => {
					if (!existing) return existing;
					return {
						adminAccounts: existing.adminAccounts.map((a) =>
							a.id === updated.id ? { ...a, ...updated } : a,
						),
					};
				},
			);
		},
	});
}
